const PLACES_BASE_URL = "https://places.googleapis.com/v1";

const GOOGLE_FIELDS = [
  "id",
  "name",
  "displayName",
  "formattedAddress",
  "addressComponents",
  "location",
  "rating",
  "userRatingCount",
  "internationalPhoneNumber",
  "nationalPhoneNumber",
  "websiteUri",
  "googleMapsUri",
  "regularOpeningHours",
  "photos",
].join(",");

const textSearchFields = GOOGLE_FIELDS.split(",").map((field) => `places.${field}`).join(",");

const getConfig = () => ({
  endpoint: process.env.REACT_APP_GOOGLE_PLACES_IMPORT_ENDPOINT || "",
  apiKey: process.env.REACT_APP_GOOGLE_PLACES_API_KEY || "",
});

const textValue = (value) => String(value || "").trim();

export const hasGoogleRedirectPattern = (input = "") => {
  const value = textValue(input).toLowerCase();
  if (!value) return false;

  return [
    "share.google",
    "maps.app.goo.gl",
    "goo.gl/maps",
    "google.com/url",
    "google.co.in/url",
  ].some((pattern) => value.includes(pattern));
};

const safeDecode = (value = "") => {
  try {
    return decodeURIComponent(value.replace(/\+/g, " "));
  } catch {
    return value;
  }
};

const cleanGooglePathPart = (value = "") =>
  safeDecode(value)
    .replace(/\/data=.*/, "")
    .replace(/@[-.\d,]+.*/, "")
    .replace(/\s+/g, " ")
    .trim();

const getUrlSearchValue = (url) =>
  url.searchParams.get("q") ||
  url.searchParams.get("query") ||
  url.searchParams.get("query_place_id") ||
  url.searchParams.get("destination") ||
  url.searchParams.get("hotel") ||
  url.searchParams.get("entity") ||
  url.searchParams.get("cid") ||
  "";

const resolveRedirectUrl = async (input) => {
  if (!hasGoogleRedirectPattern(input)) {
    return input;
  }

  try {
    const response = await fetch(input, { method: "HEAD", redirect: "follow" });
    return response.url || input;
  } catch {
    return input;
  }
};

const buildSearchText = async (input) => {
  const value = (await resolveRedirectUrl(String(input || "").trim())).trim();
  if (!value) return "";

  try {
    const url = new URL(value);
    const queryValue = getUrlSearchValue(url);
    if (queryValue) return safeDecode(queryValue);

    const redirectValue = url.searchParams.get("url") || url.searchParams.get("u");
    if (redirectValue) return buildSearchText(redirectValue);

    const placeMatch = url.pathname.match(/\/maps\/place\/([^/]+)/i);
    if (placeMatch?.[1]) return cleanGooglePathPart(placeMatch[1]);

    const travelPlaceMatch = url.pathname.match(/\/travel\/hotels\/entity\/([^/?]+)/i);
    if (travelPlaceMatch?.[1]) return cleanGooglePathPart(travelPlaceMatch[1]);
  } catch {
    return value;
  }

  return value;
};

const requestGooglePlaces = async (url, { method = "GET", apiKey, fieldMask, body } = {}) => {
  const response = await fetch(url, {
    method,
    headers: {
      "Content-Type": "application/json",
      "X-Goog-Api-Key": apiKey,
      "X-Goog-FieldMask": fieldMask,
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    throw new Error("Could not import details. Please fill manually.");
  }

  return response.json();
};

const getAddressComponent = (place, types) =>
  place.addressComponents?.find((component) => types.some((type) => component.types?.includes(type)))?.longText || "";

const getLegacyAddressComponent = (place, types) =>
  place.address_components?.find((component) => types.some((type) => component.types?.includes(type)))?.long_name || "";

const normalizeOpeningHours = (regularOpeningHours) => {
  if (!regularOpeningHours) return [];
  return regularOpeningHours.weekdayDescriptions || regularOpeningHours.weekdayText || [];
};

export const normalizeImportedGooglePlace = (place, fallbackMapUrl = "") => {
  const latitude = place.location?.latitude ?? "";
  const longitude = place.location?.longitude ?? "";
  const city =
    getAddressComponent(place, ["locality"]) ||
    getAddressComponent(place, ["administrative_area_level_3"]) ||
    getAddressComponent(place, ["administrative_area_level_2"]);
  const locality =
    getAddressComponent(place, ["sublocality_level_1"]) ||
    getAddressComponent(place, ["sublocality"]) ||
    getAddressComponent(place, ["neighborhood"]);

  return {
    googlePlaceId: place.id || "",
    name: place.displayName?.text || "",
    address: place.formattedAddress || "",
    city,
    locality,
    pincode: getAddressComponent(place, ["postal_code"]),
    latitude,
    longitude,
    googleRating: place.rating ?? "",
    googleRatingsTotal: place.userRatingCount ?? "",
    phone: place.internationalPhoneNumber || place.nationalPhoneNumber || "",
    website: place.websiteUri || "",
    mapUrl: place.googleMapsUri || fallbackMapUrl,
    googleMapsUrl: place.googleMapsUri || fallbackMapUrl,
    openingHours: normalizeOpeningHours(place.regularOpeningHours),
    googlePhotos:
      place.photos?.map((photo) => ({
        name: photo.name,
        widthPx: photo.widthPx,
        heightPx: photo.heightPx,
        authorAttributions: photo.authorAttributions || [],
      })) || [],
  };
};

const normalizeMapsJsPlace = (place, fallbackMapUrl = "") => {
  const city =
    getLegacyAddressComponent(place, ["locality"]) ||
    getLegacyAddressComponent(place, ["administrative_area_level_3"]) ||
    getLegacyAddressComponent(place, ["administrative_area_level_2"]);
  const locality =
    getLegacyAddressComponent(place, ["sublocality_level_1"]) ||
    getLegacyAddressComponent(place, ["sublocality"]) ||
    getLegacyAddressComponent(place, ["neighborhood"]);

  return {
    googlePlaceId: place.place_id || "",
    name: place.name || "",
    address: place.formatted_address || "",
    city,
    locality,
    pincode: getLegacyAddressComponent(place, ["postal_code"]),
    latitude: typeof place.geometry?.location?.lat === "function" ? place.geometry.location.lat() : "",
    longitude: typeof place.geometry?.location?.lng === "function" ? place.geometry.location.lng() : "",
    googleRating: place.rating ?? "",
    googleRatingsTotal: place.user_ratings_total ?? "",
    phone: place.international_phone_number || place.formatted_phone_number || "",
    website: place.website || "",
    mapUrl: place.url || fallbackMapUrl,
    googleMapsUrl: place.url || fallbackMapUrl,
    openingHours: place.opening_hours?.weekday_text || [],
    googlePhotos:
      place.photos?.map((photo) => ({
        name: photo.getUrl ? photo.getUrl({ maxWidth: 1200 }) : "",
        widthPx: photo.width || null,
        heightPx: photo.height || null,
        authorAttributions: photo.html_attributions || [],
      })) || [],
  };
};

const importViaConfiguredEndpoint = async ({ endpoint, input, name, city, url }) => {
  const response = await fetch(endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ input, name, city, url }),
  });

  if (!response.ok) {
    throw new Error("Could not import details. Please fill manually.");
  }

  const data = await response.json();
  return data.place ? normalizeImportedGooglePlace(data.place, input) : data;
};

const importViaBrowserKey = async ({ apiKey, input }) => {
  const searchText = await buildSearchText(input);
  if (!searchText) {
    throw new Error("Could not import details. Please fill manually.");
  }

  const searchResult = await requestGooglePlaces(`${PLACES_BASE_URL}/places:searchText`, {
    method: "POST",
    apiKey,
    fieldMask: textSearchFields,
    body: { textQuery: searchText, maxResultCount: 1, regionCode: "IN" },
  });
  const place = searchResult.places?.[0];

  if (!place?.id) {
    throw new Error("Could not import details. Please fill manually.");
  }

  const details = await requestGooglePlaces(`${PLACES_BASE_URL}/places/${place.id}`, {
    apiKey,
    fieldMask: GOOGLE_FIELDS,
  });

  return normalizeImportedGooglePlace(details, input);
};

let mapsJsPromise = null;

const loadMapsJsPlaces = (apiKey) => {
  if (window.google?.maps?.places) {
    return Promise.resolve(window.google.maps);
  }

  if (!mapsJsPromise) {
    mapsJsPromise = new Promise((resolve, reject) => {
      const callbackName = `decorzoomGooglePlacesReady${Date.now()}`;
      window[callbackName] = () => {
        delete window[callbackName];
        resolve(window.google.maps);
      };

      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places&callback=${callbackName}`;
      script.async = true;
      script.onerror = () => {
        delete window[callbackName];
        reject(new Error("Could not import details. Please fill manually."));
      };
      document.head.appendChild(script);
    });
  }

  return mapsJsPromise;
};

const placesServiceRequest = (service, method, request) =>
  new Promise((resolve, reject) => {
    service[method](request, (result, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK && result) {
        resolve(result);
        return;
      }
      reject(new Error("Could not import details. Please fill manually."));
    });
  });

const importViaMapsJs = async ({ apiKey, input }) => {
  const searchText = await buildSearchText(input);
  if (!searchText) {
    throw new Error("Could not import details. Please fill manually.");
  }

  await loadMapsJsPlaces(apiKey);
  const service = new window.google.maps.places.PlacesService(document.createElement("div"));
  const match = await placesServiceRequest(service, "findPlaceFromQuery", {
    query: searchText,
    fields: ["place_id"],
  });
  const placeId = match?.[0]?.place_id;

  if (!placeId) {
    throw new Error("Could not import details. Please fill manually.");
  }

  const details = await placesServiceRequest(service, "getDetails", {
    placeId,
    fields: [
      "place_id",
      "name",
      "formatted_address",
      "address_components",
      "geometry",
      "rating",
      "user_ratings_total",
      "international_phone_number",
      "formatted_phone_number",
      "website",
      "url",
      "opening_hours",
      "photos",
    ],
  });

  return normalizeMapsJsPlace(details, input);
};

const hasUsefulPlacePayload = (place = {}) =>
  Boolean(
    textValue(place.name) ||
      textValue(place.displayName?.text) ||
      textValue(place.formattedAddress) ||
      textValue(place.address) ||
      textValue(place.formatted_address) ||
      textValue(place.googleMapsUri) ||
      textValue(place.googleMapsUrl) ||
      textValue(place.mapUrl)
  );

const buildImportInputs = ({ name, city, url }) => {
  const directUrl = textValue(url);
  const textQuery = [name, city].map(textValue).filter(Boolean).join(", ");
  return Array.from(new Set([directUrl, textQuery].filter(Boolean)));
};

export const importGooglePlaceDetails = async (nameOrInput, city = "", url = "") => {
  const config = getConfig();
  const name = textValue(nameOrInput);
  const inputs = buildImportInputs({ name, city, url });

  if (!inputs.length) {
    throw new Error("Could not import details. Please fill manually.");
  }

  let lastError = null;

  for (const input of inputs) {
    try {
      if (config.endpoint) {
        const imported = await importViaConfiguredEndpoint({ endpoint: config.endpoint, input, name, city, url });
        if (hasUsefulPlacePayload(imported)) return imported;
        throw new Error("Google returned an empty place profile.");
      }

      if (config.apiKey) {
        try {
          const imported = await importViaBrowserKey({ apiKey: config.apiKey, input });
          if (hasUsefulPlacePayload(imported)) return imported;
          throw new Error("Google returned an empty place profile.");
        } catch (error) {
          lastError = error;
          const imported = await importViaMapsJs({ apiKey: config.apiKey, input });
          if (hasUsefulPlacePayload(imported)) return imported;
          throw new Error("Google returned an empty place profile.");
        }
      }
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError || new Error("Could not import details. Please fill manually.");
};

// Resolves a few googlePhotos entries (returned by importGooglePlaceDetails) into
// displayable image URLs, for preview purposes only — not stored anywhere. The Maps JS
// fallback path already returns a usable URL in `.name`; the Places API (New) path returns
// a photo resource name that needs one more call to resolve to an actual image URL.
export const resolveGooglePhotoPreviewUrls = async (googlePhotos = [], { maxCount = 4 } = {}) => {
  const config = getConfig();
  const candidates = googlePhotos.slice(0, maxCount);

  const urls = await Promise.all(
    candidates.map(async (photo) => {
      if (!photo?.name) return "";
      if (/^https?:\/\//i.test(photo.name)) return photo.name;
      if (!config.apiKey) return "";

      try {
        const response = await fetch(
          `${PLACES_BASE_URL}/${photo.name}/media?maxHeightPx=600&skipHttpRedirect=true&key=${config.apiKey}`
        );
        if (!response.ok) return "";
        const data = await response.json();
        return data.photoUri || "";
      } catch {
        return "";
      }
    })
  );

  return urls.filter(Boolean);
};
